/* eslint-disable max-params */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportImportPatentsService, ImportPatentsOptions } from '../import-patents-service';
import { Emitter, Logger, type Event } from '@patsnap/tahiti-core';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { FtoReportImportPatentsModel } from '../../models/import-patents-model';
import { FtoReportImportPatentsRepository } from '../../repositories/import-patents-repository';
import { type ImportPatentsStatus } from '../../repositories/import-patents-repository';
import { FtoReportComplexRetrievalService } from '../complex-retrieval-service';
import { FtoReportImportPatentsApi } from '../../apis/import-patents-api';
import { DraftingApiServiceImpl } from '@/infrastructure/api-functions/core-drafting-api/impls/drafting-api-service';
import type { WorkspaceInfo } from '../../types';

@injectable()
export class FtoReportImportPatentsServiceImpl implements FtoReportImportPatentsService {
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(FtoReportImportPatentsModel)
        private readonly _importPatentsModel: FtoReportImportPatentsModel,
        @inject(FtoReportImportPatentsRepository)
        private readonly _importPatentsRepository: FtoReportImportPatentsRepository,
        @inject(FtoReportComplexRetrievalService)
        private readonly _complexRetrievalService: FtoReportComplexRetrievalService,
        @inject(FtoReportImportPatentsApi)
        private readonly _importPatentsApi: FtoReportImportPatentsApi,
        @inject(DraftingApiServiceImpl)
        private readonly _draftingApi: DraftingApiServiceImpl<any>,
    ) { }

    private get _taskId() {
        return this._currentTaskService.taskId;
    }

    private _onImportPatentsStart: Emitter<ImportPatentsStatus> = new Emitter();
    private _onImportPatentsProgress: Emitter<ImportPatentsStatus> = new Emitter();
    private _onImportPatentsComplete: Emitter<ImportPatentsStatus> = new Emitter();

    readonly onImportPatentsStart: Event<ImportPatentsStatus> = this._onImportPatentsStart.event;
    readonly onImportPatentsProgress: Event<ImportPatentsStatus> = this._onImportPatentsProgress.event;
    readonly onImportPatentsComplete: Event<ImportPatentsStatus> = this._onImportPatentsComplete.event;

    private _lastPatentIds: string[] = [];

    private _updateStatus(status: Partial<ImportPatentsStatus>) {
        this._importPatentsModel.update((statusData) => {
            this._lastPatentIds = statusData.newCompletedPatentIds || [];
            return {
                ...statusData,
                ...status,
            };
        });
    }

    private _abortController?: AbortController;

    /**
     * 导入专利
     * @param options 从workspace导入专利选项
     * @returns 导入专利异步任务状态
     */
    async importPatents(options: ImportPatentsOptions = {}): Promise<ImportPatentsStatus> {
        // if (this._importPatentsModel.modelData.folderId === options?.folderId) {
        //     this._logger.warn('正在导入专利，请等待导入完成');
        //     return this._importPatentsModel.modelData;
        // }

        const { folderId, spaceId, batchSize } = options;

        this._abortController = new AbortController();

        this._lastPatentIds = [];

        const status = await this._importPatentsRepository.importPatents(this._taskId, {
            folderId,
            spaceId,
            batchSize,
            signal: this._abortController.signal,
            onImportPatentsStart: (status) => {
                this._updateStatus(status);
                return this._onImportPatentsStart.fire(status);
            },
            onImportPatentsProgress: (status) => {
                this._updateStatus(status);
                this._checkPatentAdded(status);
                return this._onImportPatentsProgress.fire(status);
            },
            onImportPatentsComplete: (status) => {
                this._updateStatus(status);
                return this._onImportPatentsComplete.fire(status);
            },
        });
        return status;
    }

    async syncWorkspaceInfo() {
        try {
            const res = (await this._draftingApi.getTaskContent(this._taskId, 'workspace_info') || {}) as any;
            const wsInfo = {
                spaceId: res.space_id,
                folderId: res.folder_id,
                folderPath: res.folder_path,
                batchSize: res.batch_size,
            };
            this._updateStatus(wsInfo);
            return wsInfo;
        } catch (error) {
            console.error('syncWorkspaceInfo', error);
            // ignore
        }
    }

    async updateWorkspaceInfo(wsInfo: WorkspaceInfo) {
        const res = await this._draftingApi.updateTaskContent({
            taskId: this._taskId,
            contentType: 'workspace_info',
            content: {
                space_id: wsInfo.spaceId,
                folder_id: wsInfo.folderId,
                folder_path: wsInfo.folderPath,
                batch_size: wsInfo.batchSize,
            } as any,
        });
        this._updateStatus(wsInfo);
        return res;
    }

    private async _checkPatentAdded(status: ImportPatentsStatus) {
        const lastPatentIds = this._lastPatentIds;
        const newPatentIds = status.newCompletedPatentIds;
        if (newPatentIds.some((id) => !lastPatentIds.includes(id))) {
            await this._complexRetrievalService.search();
        }
    }

    /**
     * 检查导入专利
     * @returns 导入专利异步任务状态
     */
    async checkImportPatents(): Promise<ImportPatentsStatus> {
        const res = await this.syncWorkspaceInfo();
        if (res?.folderId) {
            await this.importPatents({
                folderId: res.folderId,
                spaceId: res.spaceId,
                batchSize: res.batchSize,
            });
        }
        return this._importPatentsModel.modelData;
    }

    dispose() {
        if (this._abortController) {
            this._abortController.abort();
        }
    }
}
